import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Button, Card, Chip, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography, IconButton } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { GameCssStyles } from '../../../components/GameManagement/GameCssStyles';
import api_service from '../../../network/api_callbacks';
import Loader from '../../../utility/Loader';
import { toast } from 'react-toastify';
import { set } from 'react-hook-form';
import QuestionEntitySelectionDialog from '../../../components/QuestionManagment/QuestionEntitySelectionDialog';
import { extractVimeoId, getVimeoPlayLink, getVimeoPlayLink2, setOnProgressCallback, uploadVideoToVimeo } from '../../../network/vimeo_network_service/Vimeo_api_callback';
import { resolveVideoPreview } from '../../../network/cloudflare_stream_service/CloudflareStream_api_callback';
import { PlayArrowRounded } from "@mui/icons-material";
import { getMediaDimensionsAndRatio } from '../../../utility/Common';
import { VIMEO_BUCKETS } from '../../../network/vimeo_network_service/Vimeo_Constant';
import ProgressModal from '../../../utility/ProgressModal';

export const weightageEnum = {
  0: 'common',  //'1'
  1: 'uncommon',    //'1.5'
  2: 'rare',    //'2'
  3: 'legendary'    //'2.5'
};

const nonEmptyString = (value) => (
  typeof value === 'string' && value.trim() !== '' ? value.trim() : ''
);

// Stored media can have a Cloudflare id or preview URL while url/aspectRatio are empty.
// Rebuild the shape the update API requires so option edits are not blocked.
const normalizeQuestionMultimedia = (media) => {
  if (!media || typeof media !== 'object') return media;

  const preview = resolveVideoPreview(media);
  const url = nonEmptyString(media.url)
    || nonEmptyString(media.previewUrl)
    || nonEmptyString(preview?.watchUrl)
    || nonEmptyString(preview?.url);

  let form = nonEmptyString(media.form) || nonEmptyString(media.mediaType) || nonEmptyString(media.type);
  if (!form && url) {
    form = 'video';
  }

  let aspectRatio = nonEmptyString(media.aspectRatio);
  if (!aspectRatio && form) {
    aspectRatio = form === 'image' || form === 'audio' ? '1:1' : '16:9';
  }

  // Detail API returns an all-null object when no Multimedia doc is linked.
  if (!url) return null;

  return {
    ...media,
    ...(url ? { url } : {}),
    ...(form ? { form } : {}),
    ...(aspectRatio ? { aspectRatio } : {}),
  };
};

function CreateQuestion(props) {

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname; //=> /questions/create-question
  const state = location.state;
  /**
  {
    "question_data": {},
    "question_filter":{},
    "from": "QuestionManagement"
  }
  */

  const [loader, setLoader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isOpenEntertainmentEntityDialog, setIsOpenEntertainmentEntityDialog] = useState(false);

  const [questionId, setQuestionId] = useState(state?.question_data?.questionId || '');
  const [questionData, setQuestionData] = useState(null);
  const [questionFilter, setQuestionFilter] = useState(null);

  const [questionStatusDropDownData, setQuestionStatusDropDownData] = useState([]);
  const [questionDifficultyDropDownData, setQuestionDifficultyDropDownData] = useState([]);
  const [gameTypeDropDownData, setGameTypeDropDownData] = useState([]);
  const [entityCategoryDropDownData, setEntityCategoryDropDownData] = useState([]);

  const [questionStatus, setQuestionStatus] = useState('');
  const [questionDifficulty, setQuestionDifficulty] = useState('');
  const [gameType, setGameType] = useState('');
  const [entityCategory, setEntityCategory] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [optionList, setOptionList] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [vimeoId, setVimeoId] = useState('');
  const [contentId, setContentId] = useState('');
  const [contentMultimedia, setContentMultimedia] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [searchContentId, setSearchContentId] = useState('');
  const [searchVimeoId, setSearchVimeoId] = useState('');
  const [uploadVideoFileOnVideoPlatform, setUploadVideoFileOnVideoPlatform] = useState(null);
  const [uploadContentByVimeoID, setUploadContentByVimeoID] = useState('');
  const [searchedContentData, setSearchedContentData] = useState(null);
  const [searchedContentVideoSrc, setSearchedContentVideoSrc] = useState(null);
  const [isPlayingSearchContentVideoSrc, setIsPlayingSearchContentVideoSrc] = useState(false);
  const [vimeoPreviewImage, setVimeoPreviewImage] = useState(null);

  useEffect(() => {
    console.log("CreateQuestion.jsx - useEffect - state:questionId => ", questionId);
    if (!questionData && !questionFilter) {
      getQuestionsFilters();
      if (questionId) {
        getQuestionDetails(questionId);
      }
    }
    return () => {
      console.log("CreateQuestion.jsx - useEffect cleanup - resetting question details and filters");
      setQuestionId('');
      setQuestionData(null);
      setQuestionFilter(null);
      setQuestionStatusDropDownData([]);
      setQuestionDifficultyDropDownData([]);
      setGameTypeDropDownData([]);
      setEntityCategoryDropDownData([]);
      setQuestionStatus('');
      setQuestionDifficulty('');
      setGameType('');
      setEntityCategory('');
      setQuestionText('');
      setOptionList([]);
      setCorrectAnswer(null);
      setVimeoId('');
      setContentId('');
      setContentMultimedia(null);
      setSelectedEntity(null);
      setVideoSrc(null);
      setIsPlaying(false);
      setSearchContentId('');
      setSearchVimeoId('');
      setUploadVideoFileOnVideoPlatform(null);
      setUploadContentByVimeoID('');
      setSearchedContentData(null);
      setSearchedContentVideoSrc(null);
      setIsPlayingSearchContentVideoSrc(false);
      setVimeoPreviewImage(null);
    }
  }, []);

  // Sync selected status with dropdown data after it is set
  useEffect(() => {
    if (questionFilter) {
      if (questionFilter) {
        setQuestionStatusDropDownData(questionFilter?.status || []);
        setQuestionDifficultyDropDownData(questionFilter?.difficulty || []);
        setGameTypeDropDownData(questionFilter?.gameType || []);
        setEntityCategoryDropDownData(questionFilter?.entityType || []);

        if (!questionId) {
          console.log("CreateQuestion.jsx - getQuestionsFilters - No questionId, setting default dropdown values");
          setQuestionStatus(questionFilter?.status?.[0] || '');
          setQuestionDifficulty(null);
          //setGameType([questionFilter?.gameType?.[0],questionFilter?.gameType?.[1]] || '');
          setGameType([]);
          setEntityCategory('');
        }

        if (questionData) {
          console.log("questionData : ", questionData)
          const vimeoId = extractVimeoId(questionData?.multimedia?.url);
          setQuestionDifficulty(questionData?.difficulty || '');
          setGameType(questionData?.gameType || '');
          setQuestionStatus(questionData?.status || '');
          setQuestionText(questionData?.questionText || '');
          setOptionList(questionData?.options || []);
          setCorrectAnswer(questionData?.answer || null);
          setVimeoId(vimeoId || '');
          setContentId(questionData?.contentId || '');
          setEntityCategory(questionData?.entities?.[0]?.entityType || '');
          setSelectedEntity(questionData?.entities?.[0] || null);
          setContentMultimedia(normalizeQuestionMultimedia(questionData?.multimedia) || null);
          const loadVideo = async () => {
            try {
              const preview = resolveVideoPreview(questionData?.multimedia);
              if (preview.type === 'cloudflare') {
                setVideoSrc(preview.embedUrl);
                return;
              }
              const { progressiveLink, playLink } = await getVimeoPlayLink(vimeoId);
              setVideoSrc(progressiveLink || playLink);
            } catch (err) {
              console.error("❌ Error fetching Vimeo link:", err);
            }
          }
          loadVideo();
        }
      }
    }
  }, [
    questionFilter,
    questionData
  ]);

  const getQuestionsFilters = async () => {
    setLoader(true);
    try {
      const response = await api_service.getQuestionFilterNew({});

      //console.log('📘 getQuestionFilterNew response :', response);

      const status = response?.status;
      const resData = response?.data;
      const responseCode = response?.responseCode;
      const errorMsg = response?.error?.message;

      if (status === 201 || status === 200 || responseCode === 200 || responseCode === 201) {
        setQuestionFilter(resData || null);
      }
      else {
        toast.error(`${errorMsg}`)
        console.log('Returning responseCode due to error:', responseCode);
        return responseCode;
      }

      //console.log('📘 getQuestionFilterNew response :', response);
      //console.log('📘 getQuestionFilterNew status :', status);
      //console.log('📘 getQuestionFilterNew data :', resData);
      //console.log('📘 getQuestionFilterNew responseCode :', responseCode);
      //console.log('📘 getQuestionFilterNew errorMsg :', errorMsg);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error('❌ Failed to fetch getQuestionFilterNew:', error);
    }
  }

  const getQuestionDetails = async (questionId) => {
    setLoader(true);
    try {
      const response = await api_service.getQuestionDetailNew({ questionId });

      //console.log('📘 getQuestionDetails response :', response);

      const status = response?.status;
      const resData = response?.data;
      const responseCode = response?.responseCode;
      const errorMsg = response?.error?.message;

      if (status === 201 || status === 200 || responseCode === 200 || responseCode === 201) {
        setQuestionId(resData?.questionId || '');
        setQuestionData(resData || null);
        // setQuestionStatus(resData?.status || '');
        // setQuestionDifficulty(resData?.difficulty || '');
        // setGameType(resData?.gameType || '');
        // setEntityCategory(resData?.entities?.entityType || '');
      }
      else {
        toast.error(`${errorMsg}`)
        console.log('Returning responseCode due to error:', responseCode);
        return responseCode;
      }

      //console.log('📘 getQuestionDetails response :', response);
      //console.log('📘 getQuestionDetails status :', status);
      //console.log('📘 getQuestionDetails data :', resData);
      //console.log('📘 getQuestionDetails responseCode :', responseCode);
      //console.log('📘 getQuestionDetails errorMsg :', errorMsg);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error('❌ Failed to fetch getQuestionDetails:', error);
    }
  }

  //GEN000026
  //1151879796
  const searchContentByContentIDandVimeoId = async () => {
    setLoader(true);
    try {
      console.log('Searching content with Content ID:', searchContentId, 'and Vimeo ID:', searchVimeoId);
      const response = await api_service.getVimeoIDorContentIDDetail({ contentId: searchContentId, vimeoId: searchVimeoId });

      console.log('📘 searchContentByContentIDandVimeoId response :', response);

      const status = response?.status;
      const resData = response?.data;
      const responseCode = response?.responseCode;
      const errorMsg = response?.error?.message;

      if (status === 201 || status === 200 || responseCode === 200 || responseCode === 201) {
        const vimeoId = extractVimeoId(resData?.url);
        const loadVideo = async () => {
          try {
            const preview = resolveVideoPreview(resData);
            if (preview.type === 'cloudflare') {
              setSearchedContentVideoSrc(preview.embedUrl);
              setContentMultimedia({
                url: resData?.url || preview.watchUrl,
                aspectRatio: resData?.aspectRatio || '16:9',
                form: 'video',
                cloudFlareVideoId: preview.id,
                previewUrl: preview.watchUrl,
              });
              setSearchedContentData(resData || null);
              setVimeoId(vimeoId || '');
              return;
            }
            const { progressiveLink, playLink } = await getVimeoPlayLink(vimeoId);
            setSearchedContentVideoSrc(progressiveLink || playLink);
            setContentMultimedia({
              url: `https://api.vimeo.com/videos/${vimeoId}`,
              aspectRatio: '16:9',
              form: 'video',
              cloudFlareVideoId: resData?.cloudFlareVideoId || null,
            })
            setSearchedContentData(resData || null);
            setVimeoId(vimeoId || '');
            // setContentId('');
          } catch (err) {
            setLoader(false);
            console.error("❌ Error fetching Vimeo link:", err);
          }
        }
        loadVideo();
      }
      else {
        setLoader(false);
        toast.error(`${errorMsg}`)
        console.log('Returning responseCode due to error:', responseCode);
        return responseCode;
      }

      //console.log('📘 searchContentByContentIDandVimeoId response :', response);
      //console.log('📘 searchContentByContentIDandVimeoId status :', status);
      //console.log('📘 searchContentByContentIDandVimeoId data :', resData);
      //console.log('📘 searchContentByContentIDandVimeoId responseCode :', responseCode);
      //console.log('📘 searchContentByContentIDandVimeoId errorMsg :', errorMsg);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error('❌ Failed to fetch searchContentByContentIDandVimeoId:', error);
    }
  }

  const updateQuestionStatus = async (nextStatus) => {
    setLoader(true);
    try {
      console.log('Updating question status with Question ID:', questionId, 'and Next Status:', nextStatus);
      const response = await api_service.updateQuestionStatus({ questionId: questionId, nextStatus: nextStatus });

      console.log('📘 updateQuestionStatus response :', response);

      const status = response?.status;
      const resData = response?.data;
      const responseCode = response?.responseCode;
      const errorMsg = response?.error?.message;

      if (status === 201 || status === 200 || responseCode === 200 || responseCode === 201) {
        toast.success(`Question status updated successfully to ${nextStatus} with Question ID: ${questionId}`);
      }
      else {
        setLoader(false);
        toast.error(`${errorMsg}`)
        console.log('Returning responseCode due to error:', responseCode);
        return responseCode;
      }

      //console.log('📘 updateQuestionStatus response :', response);
      //console.log('📘 updateQuestionStatus status :', status);
      //console.log('📘 updateQuestionStatus data :', resData);
      //console.log('📘 updateQuestionStatus responseCode :', responseCode);
      //console.log('📘 updateQuestionStatus errorMsg :', errorMsg);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error('❌ Failed to update question status:', error);
    }
  }

  const questionCreateAndUpdateHandler = async () => {
    console.log("questionCreateAndUpdateHandler called with questionId:", questionId);

    // Transform gameType: replace any containing 'rapFire' with 'RF', 'rapWheel' with 'RW', remove duplicates
    let transformedGameType = Array.isArray(gameType) ? [...gameType] : (gameType ? [gameType] : []);
    transformedGameType = transformedGameType.map(g => {
      if (!g) return g;
      const val = g.toString().toLowerCase();
      if (val.includes('rapfire')) return 'RF';
      if (val.includes('rapwheel')) return 'RW';
      return g;
    });
    // Remove duplicates
    transformedGameType = [...new Set(transformedGameType.filter(Boolean))];

    const multimedia = normalizeQuestionMultimedia(contentMultimedia);

    var requestBody = {
      entityName: selectedEntity?.displayName || '',
      entityType: selectedEntity?.entityType || '',
      displayImage: selectedEntity?.displayImage || '',
      weightage: selectedEntity?.weightage || 0,
      questionText: questionText || '',
      difficulty: typeof questionDifficulty === 'number' ? questionDifficulty : (typeof questionDifficulty === 'string' && questionDifficulty !== '' ? Number(questionDifficulty) : (typeof questionData?.difficulty === 'number' ? questionData.difficulty : 0)),
      gameType: transformedGameType,
      answer: correctAnswer,
      options: optionList,
      multimedia,
    }

    console.log("questionCreateAndUpdateHandler - requestBody:", requestBody);

    // Validation for each key/value
    const errors = [];
    if (!requestBody.entityName || typeof requestBody.entityName !== 'string' || requestBody.entityName.trim() === '') {
      errors.push('Entity Name is required and must be a non-empty string.');
    }
    if (!requestBody.entityType || typeof requestBody.entityType !== 'string' || requestBody.entityType.trim() === '') {
      errors.push('Entity Type is required and must be a non-empty string.');
    }
    // if (!requestBody.displayImage || typeof requestBody.displayImage !== 'string' || requestBody.displayImage.trim() === '') {
    //   errors.push('Entity Image is required and must be a non-empty string.');
    // }
    // if (!requestBody.weightage) {
    //   errors.push('Weightage is required and must be a number.');
    // }
    // if (typeof requestBody.weightage !== 'number' || requestBody.weightage < 0) {
    //   errors.push('Weightage must be a non-negative number.');
    // }
    if (!requestBody.questionText || typeof requestBody.questionText !== 'string' || requestBody.questionText.trim() === '') {
      errors.push('Question Text is required and must be a non-empty string.');
    }
    if (typeof requestBody.difficulty !== 'number' || ![0, 1, 2].includes(requestBody.difficulty)) {
      errors.push('Difficulty must be 0, 1, or 2.');
    }
    if (!Array.isArray(requestBody.gameType) || requestBody.gameType.length === 0 || !requestBody.gameType.every(gt => typeof gt === 'string' && gt.trim() !== '')) {
      errors.push('Game Type must be a non-empty array of strings.');
    }
    if (!Array.isArray(requestBody.answer) || requestBody.answer.length === 0 || !requestBody.answer.every(ans => ans && typeof ans.id === 'string' && ans.id.trim() !== '')) {
      errors.push('Answer must be a non-empty array with objects containing non-empty label and text strings.');
    }
    if (!Array.isArray(requestBody.options) || requestBody.options.length !== 4 || !requestBody.options.every(opt => opt && typeof opt.label === 'string' && opt.label.trim() !== '' && typeof opt.text === 'string' && opt.text.trim() !== '')) {
      errors.push('Options must be an array of 4 objects with non-empty label and text strings.');
    }
    if (questionId && !requestBody.multimedia) {
      delete requestBody.multimedia;
    }
    else if (!requestBody.multimedia || typeof requestBody.multimedia !== 'object' || !requestBody.multimedia.url || typeof requestBody.multimedia.url !== 'string' || requestBody.multimedia.url.trim() === '' || !requestBody.multimedia.aspectRatio || typeof requestBody.multimedia.aspectRatio !== 'string' || requestBody.multimedia.aspectRatio.trim() === '' || !requestBody.multimedia.form || typeof requestBody.multimedia.form !== 'string' || requestBody.multimedia.form.trim() === '') {
      errors.push('Multimedia must be an object with non-empty url, aspectRatio, and form strings.');
    }

    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      console.error('Validation errors:', errors);
      return;
    }

    if (questionId) {
      // Update Question
      setLoader(true);
      try {
        console.log('Updating question with Question ID:', questionId, 'and request body:', requestBody);
        const response = await api_service.updateQuestion({ questionId: questionId, data: requestBody });

        console.log('📘 updateQuestion response :', response);

        const status = response?.status;
        const resData = response?.data;
        const responseCode = response?.responseCode;
        const errorMsg = response?.error?.message;

        if (status === 201 || status === 200 || responseCode === 200 || responseCode === 201) {
          toast.success(`Question updated successfully with Question ID: ${questionId}`);
          // Back to question list after creation
          navigate('/questions', { replace: true, state: { shouldRefresh: true } });
        }
        else {
          setLoader(false);
          toast.error(`${errorMsg}`)
          console.log('Returning responseCode due to error:', responseCode);
          return responseCode;
        }

        //console.log('📘 updateQuestion response :', response);
        //console.log('📘 updateQuestion status :', status);
        //console.log('📘 updateQuestion data :', resData);
        //console.log('📘 updateQuestion responseCode :', responseCode);
        //console.log('📘 updateQuestion errorMsg :', errorMsg);
        setLoader(false);
      } catch (error) {
        setLoader(false);
        console.error('❌ Failed to update question:', error);
      }
    }
    else {
      requestBody = { ...requestBody, contentId: contentId || '', }

      if (!requestBody.contentId) {
        toast.error('Content ID is required to create a question. Please provide a valid Content ID.');
        return;
      }

      // Create Question
      setLoader(true);
      try {
        console.log('Creating question with Question ID:', questionId, 'and request body:', requestBody);
        const response = await api_service.createQuestion({ data: requestBody });

        console.log('📘 createQuestion response :', response);

        const status = response?.status;
        const resData = response?.data;
        const responseCode = response?.responseCode;
        const errorMsg = response?.error?.message;

        if (status === 201 || status === 200 || responseCode === 200 || responseCode === 201) {
          toast.success(`Question created successfully with Question ID: ${questionId}`);
          // Back to question list after creation, trigger refresh
          navigate('/questions', { replace: true, state: { shouldRefresh: true } });
        }
        else {
          setLoader(false);
          toast.error(`${errorMsg}`)
          console.log('Returning responseCode due to error:', responseCode);
          return responseCode;
        }

        //console.log('📘 createQuestion response :', response);
        //console.log('📘 createQuestion status :', status);
        //console.log('📘 createQuestion data :', resData);
        //console.log('📘 createQuestion responseCode :', responseCode);
        //console.log('📘 createQuestion errorMsg :', errorMsg);
        setLoader(false);
      } catch (error) {
        setLoader(false);
        console.error('❌ Failed to create question:', error);
      }
    }
  }

  const getDifficultyLevelString = (difficulty) => {
    switch (difficulty) {
      case 0:
        return "Easy";
      case 1:
        return "Medium";
      case 2:
        return "Difficult";
      default:
        return "Unknown";
    }
  }

  const getDifficultyLevelValue = (difficulty) => {
    const difficultyLevel = difficulty?.toString().toLowerCase();
    switch (difficultyLevel) {
      case "easy":
        return 0;
      case "medium":
        return 1;
      case "difficult":
        return 2;
      default:
        return "Unknown";
    }
  }

  // --- Content Search and Upload Methods ---
  const handleSearchContent = () => {
    // Implement search logic here
    // Use searchContentId and searchVimeoId
    console.log('Searching content by Content ID:', searchContentId, 'Vimeo ID:', searchVimeoId);
    searchContentByContentIDandVimeoId();
  };

  const handleUploadFileChange = (e) => {
    // Implement file selection logic here
    const file = e.target.files && e.target.files[0];
    setUploadVideoFileOnVideoPlatform(file);
    uploadFileOnVimeo(e, file);
    console.log('Selected file for upload:', file);
  };

  const uploadFileOnVimeo = async (e, file) => {
    try {
      setUploading(true);
      setProgress(0);
      setOnProgressCallback((percent) => setProgress(percent));
      const { width, height, mediaType, ratio } = await getMediaDimensionsAndRatio(file);
      const resp = await uploadVideoToVimeo(file, VIMEO_BUCKETS.VIMEO_BUCKET_ENTITY_VIDEOS_STAGING, mediaType);
      console.log("VIMEO 1 : ", resp)
      const lastSegment = resp?.videoUri;
      const finalUrl = "https://api.vimeo.com" + lastSegment;
      console.log("VIMEO 2 : ", finalUrl)
      const vimeoId = extractVimeoId(finalUrl);
      console.log("vimeoId : ", vimeoId)
      const videoInfo = await getVimeoPlayLink2(vimeoId);

      if (!videoInfo) {
        console.error(`No playable Vimeo link found for video ID: ${vimeoId}`);
        return; // or show error to user
      }

      const { progressiveLink, playLink, videoData } = videoInfo;

      console.log("progressiveLink 2 : ", progressiveLink)
      console.log("playLink 2 : ", playLink)
      console.log("videoData 2 : ", videoData)

      //setSearchedContentVideoSrc(videoInfo);
      setContentMultimedia({
        url: `https://api.vimeo.com/videos/${vimeoId}`,
        aspectRatio: '16:9',
        form: mediaType === 'video' ? 'video' : (mediaType === 'audio' ? 'audio' : 'unknown'),
      })
      setSearchedContentData(progressiveLink || null);
      setVimeoId(vimeoId || '');

      setVideoSrc(progressiveLink || playLink);
      console.log("progressiveLink : ", progressiveLink)

      const title = videoData?.name;
      const duration = videoData?.duration;
      const image = videoData?.pictures?.base_link;
      e.target.value = null; // reset input
    } catch (err) {
      console.error("❌ Error fetching Vimeo link:", err);
      setUploading(false)
    }
    finally {
      setUploading(false)
    }
  }

  const previewVideoBasedOnVimeoID = async () => {
    const vimeoId = extractVimeoId(uploadContentByVimeoID)
    const loadVideo = async () => {
      try {
        const { progressiveLink, playLink } = await getVimeoPlayLink(vimeoId);

        console.log("progressiveLink 1 : ", progressiveLink)
        console.log("playLink 1 : ", playLink)
        
        setSearchedContentVideoSrc(progressiveLink || playLink);
        setContentMultimedia({
          url: `https://api.vimeo.com/videos/${vimeoId}`,
          aspectRatio: '16:9',
          form: 'video',
        })
        setSearchedContentData({ 'url': uploadContentByVimeoID } || null);
        setVimeoPreviewImage()
        setVimeoId(vimeoId || '');
      } catch (err) {
        console.error("❌ Error fetching Vimeo link:", err);
      }
    }
    loadVideo();
  }

  return (
    <>
      <div style={styles.container}>
        <Box sx={{ flex: 1, height: "100%", minHeight: 0, display: "flex", mt: '5vh', flexDirection: "column", padding: 5, justifyContent: 'flex-start' }}>

          {/* Row 1 : Question ID and Question Type */}
          <Box sx={{ flex: 0, display: 'flex', height: 'auto', width: '100%', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              {questionId && <>
                <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}>Question ID :</Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 800, fontSize: 14, color: '#000000', ml: 1 }}>{questionId}</Typography>
              </>}
            </Box>
            {(questionStatusDropDownData?.length > 0) && <Box sx={{ flex: 0.2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <FormControl
                fullWidth
                margin="normal">
                <InputLabel sx={GameCssStyles.inputLabelStyle_sx}>Question Status</InputLabel>
                <Select
                  value={questionStatus}
                  onChange={(e) => {
                    //console.log("answer release : ",e.target.value)
                    setQuestionStatus(e.target.value)
                    updateQuestionStatus(e.target.value);
                  }}
                  label="Question Status"
                  sx={GameCssStyles.formFiledStyle_sx2}
                >
                  <MenuItem value="" disabled>
                    Select Question Status
                  </MenuItem>
                  {questionStatusDropDownData?.map((item, idx) => {
                    return (<MenuItem key={idx} value={item} sx={GameCssStyles.menuItemStyle_sx}> {item?.toUpperCase()} </MenuItem>)
                  })}
                </Select>
              </FormControl>
            </Box>}
          </Box>

          {/* Row 2 : Vimeo ID or Content ID with Video Player*/}
          <Box sx={{ flex: 0, display: 'flex', height: 'auto', width: '100%', mt: 5, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 5 }}>
            <Box sx={{ flex: 0.5, display: 'flex', width: '100%', height: 'auto', flexDirection: 'column', alignSelf: 'center', gap: 3, }}>

              {/* Row 6 : Entity Details*/}
              <Box sx={{ flex: 1, display: 'flex', height: 'auto', width: '100%', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', mt: -1 }}>
                {(!selectedEntity || !selectedEntity.entityId) ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      border: '2px dashed #aaa',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#888',
                      background: '#faf7ff',
                      transition: 'border 0.2s',
                      '&:hover': { border: '2px solid #8F51FB', color: '#8F51FB' },
                    }}
                    onClick={() => setIsOpenEntertainmentEntityDialog(true)}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>+ Add Entity</Typography>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>Click to select an entity</Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: 120,
                      border: '2px solid #8F51FB',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      background: '#faf7ff',
                      position: 'relative',
                      p: 2,
                      boxShadow: 1,
                    }}
                  >
                    {/* Entity Image */}
                    {selectedEntity.displayImage && (
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: 2,
                          overflow: 'hidden',
                          background: '#eee',
                          mr: 2,
                          flexShrink: 0,
                          backgroundImage: `url(${selectedEntity.displayImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    {/* Entity Details */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>ID:</b> {selectedEntity.entityId}</Typography>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>Name:</b> {selectedEntity.displayName}</Typography>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>Type:</b> {selectedEntity.entityType}</Typography>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>Weightage:</b> {weightageEnum[selectedEntity.weightage]}</Typography>
                    </Box>
                    {/* Remove Button */}
                    <Button
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        minWidth: 0,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: '#fff',
                        color: '#8F51FB',
                        fontWeight: 900,
                        fontSize: 18,
                        p: 0,
                        zIndex: 2,
                        boxShadow: 1,
                        '&:hover': { background: '#f5eaff', color: '#d32f2f' },
                      }}
                      onClick={() => setSelectedEntity(null)}
                    >
                      ×
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Row 3 : Difficulty & Game Type*/}
              <Box sx={{ flex: 1, display: 'flex', height: 'auto', width: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', mt: -2 }}>
                {/* Difficulty Level Dropdown*/}
                <FormControl
                  fullWidth
                  margin="normal">
                  <InputLabel sx={GameCssStyles.inputLabelStyle_sx}>Difficulty Level</InputLabel>
                  <Select
                    value={typeof questionDifficulty === 'string' ? Number(questionDifficulty) : questionDifficulty}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestionDifficulty(typeof val === 'string' ? Number(val) : val);
                    }}
                    label="Difficulty Level"
                    sx={GameCssStyles.formFiledStyle_sx2}
                  >
                    <MenuItem
                      value=""
                      disabled>
                      Select Difficulty Level
                    </MenuItem>
                    {questionDifficultyDropDownData?.map((item, idx) => {
                      //console.log("Mapping difficulty dropdown item:", item, "questionDifficulty value:", questionDifficulty, " questionData difficulty:", questionData?.difficulty);
                      return (<MenuItem key={idx} value={item} sx={GameCssStyles.menuItemStyle_sx}> {getDifficultyLevelString(item)} </MenuItem>)
                    })}
                  </Select>
                </FormControl>

                {/* Game Type Dropdown with multiple selected GAMES*/}
                <FormControl fullWidth margin="normal" sx={{ ml: 0 }}>
                  <InputLabel sx={GameCssStyles?.inputLabelStyle_sx}>Game Type</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(gameType) ? gameType : gameType ? [gameType] : []}
                    onChange={(e) => {
                      setGameType(e.target.value);
                    }}
                    label="Game Type"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                    sx={GameCssStyles?.formFiledStyle_sx2}
                  >
                    <MenuItem disabled value="">
                      Select Game Type
                    </MenuItem>
                    {gameTypeDropDownData?.map((item, idx) => (
                      <MenuItem key={idx} value={item} sx={GameCssStyles?.menuItemStyle_sx}>
                        <input
                          type="checkbox"
                          checked={Array.isArray(gameType) ? gameType.indexOf(item) > -1 : gameType === item}
                          readOnly
                          style={{ marginRight: 8 }}
                        />
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

              </Box>

              <TextField
                fullWidth
                label="Content ID"
                disabled={questionId ? true : false}
                value={contentId || ''}
                onChange={e => {
                  setContentId(e.target.value);
                }}
                sx={GameCssStyles.formSingleLineTextField_sx}
                inputProps={{}}
              />
              <TextField
                fullWidth
                label="Vimeo ID"
                disabled={true}
                value={vimeoId || ''}
                onChange={e => {
                  setVimeoId(e.target.value);
                }}
                sx={GameCssStyles.formSingleLineTextField_sx}
                inputProps={{ readOnly: true }}
              />

            </Box>

            {(!contentMultimedia && !searchedContentData) ?
              (
                <Box
                  sx={{
                    flex: 0.5,
                    height: 470,
                    border: '2px dashed #aaa',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#888',
                    background: '#faf7ff',
                    transition: 'border 0.2s',
                    gap: 4,
                    p: 4,
                  }}
                >
                  {/* Option 1: Search Content */}
                  <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    <>
                      <TextField
                        label="Content ID"
                        value={searchContentId}
                        onChange={e => setSearchContentId(e.target.value)}
                        sx={{ ...GameCssStyles.formSingleLineTextField_sx, width: '80%' }}
                      />
                      <TextField
                        label="Vimeo ID"
                        value={searchVimeoId}
                        onChange={e => setSearchVimeoId(e.target.value)}
                        sx={{ ...GameCssStyles.formSingleLineTextField_sx, width: '80%' }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 1, width: '80%' }}
                        onClick={handleSearchContent}
                      >
                        Search Content
                      </Button>
                    </>
                  </Box>

                  {/* Option 2: Upload Multimedia */}
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>Upload Multimedia to Vimeo</Typography>
                    <Box sx={{ width: '80%', display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', mt: 2 }}>
                      <TextField
                        label="Enter Vimeo ID"
                        value={uploadContentByVimeoID}
                        onChange={e => setUploadContentByVimeoID(e.target.value)}
                        sx={{ ...GameCssStyles.formSingleLineTextField_sx, width: '80%' }}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ width: 'auto', height: '100%', fontSize: 14, }}
                        onClick={() => {
                          previewVideoBasedOnVimeoID();
                        }}
                      >
                        Preview
                      </Button>
                    </Box>
                    <Button
                      variant="contained"
                      color="secondary"
                      component="label"
                      sx={{ width: '80%', height: 40, fontSize: 14, }}
                    >
                      Upload Local File to Vimeo (Preview not available)
                      <input
                        type="file"
                        hidden
                        onChange={handleUploadFileChange}
                      />
                    </Button>
                  </Box>
                </Box>
              )
              :
              (
                <Box
                  sx={{
                    flex: 0.5,
                    height: 450,
                    // border: '2px solid #8F51FB',
                    // borderRadius: 2.5,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    // background: '#faf7ff',
                    position: 'relative',
                    p: 0,
                    // boxShadow: 1,
                    mr: 0,
                  }}
                >
                  {/* Video Player for Vimeo ID */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                    {(videoSrc || searchedContentVideoSrc) ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: 446,
                          // backgroundImage: !isPlaying
                          //   ? `url(${image || ""})`
                          //   : "none",
                          backgroundSize: "cover",
                          bgcolor: "rgba(0,0,0,0.6)",
                          backgroundPosition: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          // borderRadius: 2.5,
                        }}
                      >
                        {/* Overlay gradient */}
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.1))",
                            // borderRadius: 2,
                          }}
                        />
                        {(!isPlaying || !isPlayingSearchContentVideoSrc) ? (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation(); // prevent triggering onClick selection
                              setIsPlaying(true);
                              setIsPlayingSearchContentVideoSrc(true);
                            }}
                            sx={{
                              bgcolor: "rgba(0,0,0,0.6)",
                              color: "white",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                              p: 3,
                            }}
                          >
                            <PlayArrowRounded fontSize="large" />
                          </IconButton>
                        ) : (
                          (videoSrc || searchedContentVideoSrc) && (
                            String(videoSrc || searchedContentVideoSrc).includes('videodelivery.net') ||
                            String(videoSrc || searchedContentVideoSrc).includes('cloudflarestream.com') ? (
                              <iframe
                                title="video-preview"
                                src={videoSrc || searchedContentVideoSrc}
                                style={{
                                  width: "100%",
                                  height: 450,
                                  border: 0,
                                  backgroundColor: "black",
                                }}
                                allow="autoplay; fullscreen; picture-in-picture"
                                frameBorder="0"
                              />
                            ) : (
                              <video
                                src={videoSrc || searchedContentVideoSrc}
                                controls
                                autoPlay
                                playsInline
                                controlsList="nodownload"
                                style={{
                                  width: "100%",
                                  height: 450,
                                  // borderRadius: 2.5,
                                  backgroundColor: "black",
                                }}
                                onPause={() => {
                                  setIsPlaying(false);
                                  setIsPlayingSearchContentVideoSrc(false);
                                }}
                              />
                            )
                          )
                        )}
                      </Box>
                    ) : null}
                  </Box>
                  {/* Remove Button */}
                  <Button
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      minWidth: 0,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#fff',
                      color: '#8F51FB',
                      fontWeight: 900,
                      fontSize: 18,
                      p: 0,
                      zIndex: 2,
                      boxShadow: 1,
                      '&:hover': { background: '#f5eaff', color: '#d32f2f' },
                    }}
                    onClick={() => {
                      setContentMultimedia(null);
                      setIsPlaying(false);
                      setVideoSrc(null);
                      setSearchedContentVideoSrc(null);
                      setIsPlayingSearchContentVideoSrc(false);
                      setSearchedContentData(null);
                      setSearchVimeoId('');
                      setSearchContentId('');
                      setVimeoId('');
                    }}
                  >
                    ×
                  </Button>
                </Box>
              )}
          </Box>

          {/* Row 4 : Question Text */}
          <Box sx={{ flex: 0, display: 'flex', height: 'auto', width: '100%', mt: 8, flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
            <TextField
              label="Question Text"
              value={questionText || ''}
              onChange={(e) => setQuestionText(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={GameCssStyles.formMultiLineTextField_sx}
            />
          </Box>

          {/* Row 5 : Options With Correct Answer*/}
          <Box sx={{ flex: 0, width: '100%', flexDirection: 'column', display: 'flex', mt: 5 }}>

            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2, px: 1 }}>
              <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: 13 }}>Label</Typography>
              <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 13 }}>Option Text</Typography>
            </Box>

            {/* Options Rows */}
            {(optionList.length === 4 ? optionList : Array.from({ length: 4 }, (_, idx) => optionList[idx] || { id: `option_${idx + 1}`, label: '', text: '' })).map((opt, idx) => {
              const isSelected = Array.isArray(correctAnswer) && correctAnswer.length > 0 && correctAnswer[0]?.id === opt.id;
              // Handler to select this option as correct
              const handleSelect = () => setCorrectAnswer([{ id: opt.id }]);
              return (
                <Box
                  key={opt.id || idx}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    mb: 1,
                    p: 2,
                    px: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: isSelected ? '#a774ff' : 'background.paper',
                    border: isSelected ? '2px solid #a774ff' : '1px solid #00000020',
                    transition: 'background 0.1s, border 0.1s',
                    gap: 3
                  }}
                  onClick={handleSelect}
                >
                  <TextField
                    label="Label"
                    //value={opt.label && opt.label.trim() !== '' ? opt.label : ['A', 'B', 'C', 'D'][idx]}
                    value={opt.label || ''}
                    onChange={e => {
                      const newOptions = [...optionList];
                      if (!newOptions[idx]) newOptions[idx] = { id: opt.id, label: '', text: '' };
                      newOptions[idx].label = e.target.value;
                      setOptionList(newOptions);
                    }}
                    sx={{ flex: 0.2, mr: 1, }}
                    inputProps={{ maxLength: 1, style: isSelected ? { backgroundColor: '#a774ff', color: '#fff' } : {} }}
                    onClick={e => {
                      e.stopPropagation();
                      //handleSelect();
                    }}
                  />
                  <TextField
                    label="Option"
                    value={opt.text || ''}
                    onChange={e => {
                      const newOptions = [...optionList];
                      if (!newOptions[idx]) newOptions[idx] = { id: opt.id, label: '', text: '' };
                      newOptions[idx].text = e.target.value;
                      setOptionList(newOptions);
                    }}
                    sx={{ flex: 1, }}
                    inputProps={{ style: isSelected ? { backgroundColor: '#a774ff', color: '#fff' } : {} }}
                    onClick={e => {
                      e.stopPropagation();
                      //handleSelect();
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* <Box sx={{ flex: 1, display: 'flex', height: 'auto', width: '100%', gap: 5, mt: 4, flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Box sx={{ flex: 1, display: 'flex', height: 'auto', width: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
              <FormControl
                fullWidth
                margin="normal">
                <InputLabel sx={GameCssStyles.inputLabelStyle_sx}>Difficulty Level</InputLabel>
                <Select
                  value={typeof questionDifficulty === 'string' ? Number(questionDifficulty) : questionDifficulty}
                  onChange={(e) => {
                    const val = e.target.value;
                    setQuestionDifficulty(typeof val === 'string' ? Number(val) : val);
                  }}
                  label="Difficulty Level"
                  sx={GameCssStyles.formFiledStyle_sx2}
                >
                  <MenuItem
                    value=""
                    disabled>
                    Select Difficulty Level
                  </MenuItem>
                  {questionDifficultyDropDownData?.map((item, idx) => {
                    console.log("Mapping difficulty dropdown item:", item, "questionDifficulty value:", questionDifficulty, " questionData difficulty:", questionData?.difficulty);
                    return (<MenuItem key={idx} value={item} sx={GameCssStyles.menuItemStyle_sx}> {getDifficultyLevelString(item)} </MenuItem>)
                  })}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" sx={{ ml: 0 }}>
                <InputLabel sx={GameCssStyles?.inputLabelStyle_sx}>Game Type</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(gameType) ? gameType : gameType ? [gameType] : []}
                  onChange={(e) => {
                    setGameType(e.target.value);
                  }}
                  label="Game Type"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  sx={GameCssStyles?.formFiledStyle_sx2}
                >
                  <MenuItem disabled value="">
                    Select Game Type
                  </MenuItem>
                  {gameTypeDropDownData?.map((item, idx) => (
                    <MenuItem key={idx} value={item} sx={GameCssStyles?.menuItemStyle_sx}>
                      <input
                        type="checkbox"
                        checked={Array.isArray(gameType) ? gameType.indexOf(item) > -1 : gameType === item}
                        readOnly
                        style={{ marginRight: 8 }}
                      />
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

            </Box>

            <Box sx={{ flex: 1, display: 'flex', height: 'auto', width: '100%', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', mt: 2 }}>
              {(!selectedEntity || !selectedEntity.entityId) ? (
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    border: '2px dashed #aaa',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#888',
                    background: '#faf7ff',
                    transition: 'border 0.2s',
                    '&:hover': { border: '2px solid #8F51FB', color: '#8F51FB' },
                  }}
                  onClick={() => setIsOpenEntertainmentEntityDialog(true)}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>+ Add Entity</Typography>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>Click to select an entity</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    minHeight: 120,
                    border: '2px solid #8F51FB',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    background: '#faf7ff',
                    position: 'relative',
                    p: 2,
                    boxShadow: 1,
                    mr: 2,
                  }}
                >
                  {selectedEntity.displayImage && (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        overflow: 'hidden',
                        background: '#eee',
                        mr: 2,
                        flexShrink: 0,
                        backgroundImage: `url(${selectedEntity.displayImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>ID:</b> {selectedEntity.entityId}</Typography>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>Name:</b> {selectedEntity.displayName}</Typography>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>Type:</b> {selectedEntity.entityType}</Typography>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}><b>Weightage:</b> {selectedEntity.weightage}</Typography>
                  </Box>
                  <Button
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      minWidth: 0,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#fff',
                      color: '#8F51FB',
                      fontWeight: 900,
                      fontSize: 18,
                      p: 0,
                      zIndex: 2,
                      boxShadow: 1,
                      '&:hover': { background: '#f5eaff', color: '#d32f2f' },
                    }}
                    onClick={() => setSelectedEntity(null)}
                  >
                    ×
                  </Button>
                </Box>
              )}
            </Box>
          </Box> */}

          {/* Row 7 : Create Or Update Question*/}
          <Box sx={{ flex: 1, display: 'flex', height: 'auto', width: '100%', mt: 8, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ flex: 1, width: '100%', flexDirection: 'column', display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'flex-start', marginBottom: 5, }}>
              <Box sx={{ flex: 0, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                {questionId && <>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}>Created At :</Typography>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 800, fontSize: 14, color: '#000000', ml: 1 }}>
                    {questionData?.createdAt ? dayjs(questionData.createdAt).format('DD/MM/YYYY HH:mm') : ''}
                  </Typography>
                </>}
              </Box>
              <Box sx={{ flex: 0, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                {questionId && <>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#000000', mr: 0 }}>Updated At :</Typography>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 800, fontSize: 14, color: '#000000', ml: 1 }}>
                    {questionData?.updatedAt ? dayjs(questionData.updatedAt).format('DD/MM/YYYY HH:mm') : ''}
                  </Typography>
                </>}
              </Box>
            </Box>
            <Button
              variant="contained"
              color="success"
              sx={{
                alignSelf: 'center',
                borderRadius: 2,
                textTransform: "none",
                px: 4,
                marginBottom: 5,
              }}
              onClick={() => {
                questionCreateAndUpdateHandler();
                //setIsOpenEntertainmentEntityDialog(true);
              }}>
              {questionId ? 'Update Question' : 'Create Question'}
            </Button>
          </Box>

        </Box>
      </div >
      {/* ✅ Loader overlay in center */}
      {loader && (
        <Box
          sx={{
            position: "fixed", // ✅ fixed to viewport
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(255,255,255,0.6)",
            zIndex: 1300, // higher than sticky header

            // position: "absolute",
            // top: "50%",
            // left: "50%",
            // transform: "translate(-50%, -50%)", // ✅ Center correctly
            // display: "flex",
            // justifyContent: "center",
            // alignItems: "center",
            // background: "rgba(255,255,255,0.6)",
            // zIndex: 10,
            // width: "100%",
            // height: "100%",
          }}
        >
          <Loader />
        </Box>)}

      {/* {(loader) && <Loader />} */}
      {(uploading || loader) && <ProgressModal open={uploading || loader} isLoader={loader} progress={progress} title="Uploading File" />}

      {/* Entity Dialog */}
      <QuestionEntitySelectionDialog
        open={isOpenEntertainmentEntityDialog}
        onClose={() => {
          //console.log("multimediaDetail : ", multimediaDetail)
          setIsOpenEntertainmentEntityDialog(false)
        }}
        onSubmit={(mediaEntity) => {
          setIsOpenEntertainmentEntityDialog(false)
          console.log("mediaEntity : ", mediaEntity);
          setEntityCategory(mediaEntity?.details?.entity?.type || '');
          setSelectedEntity({
            displayName: mediaEntity?.details?.entity?.displayName || '',
            entityId: mediaEntity?.details?.entity?._id || '',
            entityType: mediaEntity?.details?.entity?.type || '',
            weightage: mediaEntity?.details?.weightage || 0,
            displayImage: mediaEntity?.details?.media?.url || '',
          } || null);
        }}
        selectedItem={selectedEntity || {}}
        handleChange={() => {

        }}
        fullScreen
      />
    </>
  );
}

const styles = {
  container: {
    flex: 1,
    height: '100vh',
    width: '100%',
    textAlign: 'center',
  },
};

export default CreateQuestion;